import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type {
  BrandContentResponseModel,
  CategoryContentResponseModel,
  CollectionContentResponseModel,
  GetCatalogProductsV1Params,
  ProductListItemDto,
  ProductSearchResultDto,
  ProductSortKey,
  ProductTagFacetDto,
  SectionContentResponseModel,
  ShopProductListApiElementContentModel,
  SubCategoryContentResponseModel,
} from '@/services/platform/api/model';
import { cn, ModuleProperties, parseShopUrlSegments, useLocaleStore } from '@/lib/core';
import { ImagesToImageMap } from '@/lib/core/utils';
import ModuleWrapper from '@/components/shared/module/wrapper';
import {
  getBrandById,
  getCatalogProducts,
  getCategoryById,
  getCollectionById,
  getSectionByShopSectionPath,
  getSubCategoryById,
} from '@/services/platform/data/shop/catalog';
import { ShopProductList } from '@/components/shared/shop/product/list';
import type { Image } from '@/lib/core/utils/image';
import { abortAfter } from '@/services/custom-fetch';

const PAGE_SIZE = 12;
const REQUEST_TIMEOUT_MS = 15000;

export const ShopProductListApi = (props: ModuleProperties) => {
  const content = props.content as ShopProductListApiElementContentModel | undefined;
  const locale = useLocaleStore(state => state.locale);
  const location = useLocation();

  const { categoryId, subcategoryId, collectionId, sectionPath, brandId } = parseShopUrlSegments(
    location.pathname
  );

  const [title, setTitle] = useState<string | undefined>(undefined);
  const [brand, setBrand] = useState<BrandContentResponseModel | undefined>(undefined);
  const [category, setCategory] = useState<CategoryContentResponseModel | undefined>(undefined);
  const [subCategory, setSubCategory] = useState<SubCategoryContentResponseModel | undefined>(undefined);
  const [collection, setCollection] = useState<CollectionContentResponseModel | undefined>(undefined);
  const [section, setSection] = useState<SectionContentResponseModel | undefined>(undefined);
  const [products, setProducts] = useState<ProductListItemDto[]>([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [categories, setCategories] = useState<ProductTagFacetDto[] | null>(null);
  const [subcategories, setSubcategories] = useState<ProductTagFacetDto[] | null>(null);
  const [subSubcategories, setSubSubcategories] = useState<ProductTagFacetDto[] | null>(null);
  const [brands, setBrands] = useState<string[] | null>(null);
  const [colors, setColors] = useState<ProductTagFacetDto[] | null>(null);
  const [minPrice, setMinPrice] = useState<number | null>(null);
  const [maxPrice, setMaxPrice] = useState<number | null>(null);
  const [facetTier, setFacetTier] = useState<'category' | 'subcategory' | 'subsubcategory'>('category');
  const [activeTabId, setActiveTabId] = useState('all');
  const [activeTabTier, setActiveTabTier] = useState<'category' | 'subcategory' | 'subsubcategory'>(
    'category'
  );
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sortBy, setSortBy] = useState<ProductSortKey | undefined>(undefined);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedColorSlugs, setSelectedColorSlugs] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number] | undefined>(undefined);
  const requestIdRef = useRef(0);

  const handleTabChange = (id: string) => {
    setActiveTabId(id);
    setActiveTabTier(facetTier);
  };

  const handleOpenFilter = () => setIsFilterOpen(true);
  const handleCloseFilter = () => setIsFilterOpen(false);

  const handleApplyFilters = (filters: {
    sortBy?: ProductSortKey;
    brands: string[];
    colorSlugs: string[];
    priceRange: [number, number];
  }) => {
    setSortBy(filters.sortBy);
    // A brand-scoped page (e.g. /shop/.../brand_xxx) locks the brand filter to that
    // one brand — the sheet's brand checkboxes are disabled, so this just guards
    // against the draft ever carrying a different value.
    setSelectedBrands(brand?.name ? [brand.name] : filters.brands);
    setSelectedColorSlugs(filters.colorSlugs);
    setPriceRange(filters.priceRange);
  };

  const handleRemoveFilters = () => {
    setSortBy(undefined);
    setSelectedBrands(brand?.name ? [brand.name] : []);
    setSelectedColorSlugs([]);
    setPriceRange(undefined);
  };

  const isPriceFilterActive = Boolean(
    priceRange && (priceRange[0] !== (minPrice ?? 0) || priceRange[1] !== (maxPrice ?? 0))
  );
  // The locked brand isn't an optional filter the user applied, so it doesn't count
  // toward the "N active filters" badge.
  const activeFilterCount =
    (sortBy ? 1 : 0) +
    selectedBrands.filter(b => b !== brand?.name).length +
    selectedColorSlugs.length +
    (isPriceFilterActive ? 1 : 0);

  const applyFacets = (result: ProductSearchResultDto | undefined) => {
    // Only the fully unfiltered fetch should (re)populate the tab list and filter
    // options — a tab- or filter-narrowed fetch's facets must not reshuffle what's shown.
    // The locked brand (on a brand-scoped page) doesn't count — it's always present
    // in `selectedBrands` and isn't a filter the user chose to narrow by.
    const hasActiveFilter =
      activeTabId !== 'all' ||
      selectedBrands.filter(b => b !== brand?.name).length > 0 ||
      selectedColorSlugs.length > 0 ||
      Boolean(priceRange);
    if (hasActiveFilter) return;
    setCategories(result?.categories ?? null);
    setSubcategories(result?.subcategories ?? null);
    setSubSubcategories(result?.subSubcategories ?? null);
    setBrands(result?.brands ?? null);
    setColors(result?.colors ?? null);
    setMinPrice(result?.minPrice ?? null);
    setMaxPrice(result?.maxPrice ?? null);
    setFacetTier(
      result?.subSubcategories?.length
        ? 'subsubcategory'
        : result?.subcategories?.length
          ? 'subcategory'
          : 'category'
    );
  };

  useEffect(() => {
    setActiveTabId('all');
    setActiveTabTier('category');
    handleRemoveFilters();
    Promise.all([
      categoryId ? getCategoryById(categoryId, locale) : Promise.resolve(undefined),
      subcategoryId ? getSubCategoryById(subcategoryId, locale) : Promise.resolve(undefined),
      collectionId ? getCollectionById(collectionId, locale) : Promise.resolve(undefined),
      sectionPath ? getSectionByShopSectionPath(sectionPath, locale) : Promise.resolve(undefined),
      brandId ? getBrandById(brandId, locale) : Promise.resolve(undefined),
    ]).then(([cat, sub, col, sec, brd]) => {
      setTitle(
        sub?.properties?.title ??
          sub?.name ??
          cat?.properties?.title ??
          cat?.name ??
          col?.name ??
          brd?.properties?.title ??
          brd?.name
      );
      setCategory(cat);
      setSubCategory(sub);
      setCollection(col);
      setSection(sec);
      setBrand(brd);
      // `handleRemoveFilters()` above ran against the *previous* route's `brand`
      // state — now that the new brand has resolved, (re)lock the brand filter to it.
      if (brd?.name) setSelectedBrands([brd.name]);
    });
  }, [categoryId, subcategoryId, collectionId, sectionPath, brandId, locale]);

  const buildProductsParams = useCallback(
    (pageNum: number): GetCatalogProductsV1Params => {
      const activeFacetSlug = activeTabId !== 'all' ? activeTabId : undefined;

      const categorySlug =
        activeTabTier === 'category'
          ? (activeFacetSlug ?? category?.properties?.slug)
          : category?.properties?.slug;
      const subcategorySlug =
        activeTabTier === 'subcategory'
          ? (activeFacetSlug ?? subCategory?.properties?.slug)
          : subCategory?.properties?.slug;
      const subsubcategorySlug = activeTabTier === 'subsubcategory' ? activeFacetSlug : undefined;

      return {
        pageSize: PAGE_SIZE,
        page: pageNum,
        section: section?.properties?.slug ? [section?.properties?.slug] : undefined,
        category: categorySlug ? [categorySlug] : undefined,
        subcategory: subcategorySlug ? [subcategorySlug] : undefined,
        subsubcategory: subsubcategorySlug ? [subsubcategorySlug] : undefined,
        collection: collection?.id ? [collection?.id] : undefined,
        brand: selectedBrands.length ? selectedBrands : undefined,
        // No dedicated `color` param exists on the catalog search API yet — color
        // slugs ride on the generic `slugs` filter instead (unused otherwise, since
        // subcategory filtering moved to its own dedicated param above).
        slugs: selectedColorSlugs.length ? selectedColorSlugs : undefined,
        priceMin: priceRange?.[0],
        priceMax: priceRange?.[1],
        sortBy,
      };
    },
    [
      category,
      subCategory,
      collection,
      section,
      activeTabId,
      activeTabTier,
      selectedBrands,
      selectedColorSlugs,
      priceRange,
      sortBy,
    ]
  );

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    const controller = new AbortController();
    const timeoutId = abortAfter(controller, REQUEST_TIMEOUT_MS);
    setPage(1);
    setIsLoading(true);
    // Reset before the fetch settles — otherwise a stale `true` left over from the
    // previous filter/tab keeps the infinite-scroll sentinel mounted and able to fire
    // a page-2 request for the new filter before this page-1 request even resolves.
    setHasNextPage(false);
    getCatalogProducts(buildProductsParams(1), locale, controller.signal)
      .then(result => {
        if (requestIdRef.current !== requestId) return;
        const items = result?.products?.items ?? [];
        setProducts(items);
        // Never paginate past an empty page — some filter combinations report
        // `hasNextPage: true` on a zero-item page, which would otherwise send
        // the infinite-scroll sentinel into an endless page-1/2/3... loop.
        setHasNextPage(items.length > 0 && (result?.products?.hasNextPage ?? false));
        applyFacets(result);
      })
      .catch(error => {
        if (requestIdRef.current !== requestId) return;
        console.error('ShopProductListApi: failed to fetch catalog products', error);
        setProducts([]);
        setHasNextPage(false);
      })
      .finally(() => {
        clearTimeout(timeoutId);
        if (requestIdRef.current === requestId) setIsLoading(false);
      });
    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [buildProductsParams, locale]);

  const handleLoadMore = useCallback(() => {
    if (loadingMore || !hasNextPage) return;
    const requestId = ++requestIdRef.current;
    const controller = new AbortController();
    const timeoutId = abortAfter(controller, REQUEST_TIMEOUT_MS);
    const nextPage = page + 1;
    setLoadingMore(true);
    getCatalogProducts(buildProductsParams(nextPage), locale, controller.signal)
      .then(result => {
        if (requestIdRef.current !== requestId) return;
        const items = result?.products?.items ?? [];
        setProducts(prev => [...prev, ...items]);
        setHasNextPage(items.length > 0 && (result?.products?.hasNextPage ?? false));
        applyFacets(result);
        setPage(nextPage);
      })
      .catch(error => {
        if (requestIdRef.current !== requestId) return;
        console.error('ShopProductListApi: failed to fetch more catalog products', error);
      })
      .finally(() => {
        clearTimeout(timeoutId);
        if (requestIdRef.current === requestId) setLoadingMore(false);
      });
  }, [loadingMore, hasNextPage, page, buildProductsParams, locale]);

  const bannerImage = collection?.properties?.imageUrl
    ? ({ src: collection?.properties?.imageUrl } as Image)
    : ImagesToImageMap(
        brand?.properties?.image ?? subCategory?.properties?.image ?? category?.properties?.image
      );

  if (!content) return null;

  return (
    <ModuleWrapper
      component={props}
      classNames={{
        base: cn(['module__shop-product-list']),
        container: cn(['flex-1']),
        wrapper: cn(['py-0', 'flex-1']),
        grid: cn(['flex flex-col flex-1 gap-0']),
      }}
      isFullWidth={true}
      {...props}
    >
      <ShopProductList
        products={products}
        title={title}
        description={
          subCategory?.properties?.description ??
          category?.properties?.description ??
          collection?.properties?.description ??
          brand?.properties?.description ??
          undefined
        }
        category={category}
        subCategory={subCategory}
        bannerImage={bannerImage}
        heroSlideshow={brand?.properties?.heroSlideshow}
        sortBy={sortBy}
        categories={categories}
        subcategories={subcategories}
        subSubcategories={subSubcategories}
        activeTabId={activeTabId}
        onTabChange={handleTabChange}
        activeFilterCount={activeFilterCount}
        brands={brands}
        colors={colors}
        minPrice={minPrice}
        maxPrice={maxPrice}
        lockedBrand={brand?.name}
        selectedBrands={selectedBrands}
        selectedColorSlugs={selectedColorSlugs}
        priceRange={priceRange ?? [minPrice ?? 0, maxPrice ?? 0]}
        isFilterOpen={isFilterOpen}
        onOpenFilter={handleOpenFilter}
        onCloseFilter={handleCloseFilter}
        onApplyFilters={handleApplyFilters}
        hasNextPage={hasNextPage}
        isLoading={isLoading}
        loadingMore={loadingMore}
        onLoadMore={handleLoadMore}
        context={props.context}
      />
    </ModuleWrapper>
  );
};
